import http from 'core/services/http';
import Config from 'core/config';
import axios from 'axios';
import isObject from 'lodash';
import { toQueryString } from 'shared/helpers';

class RoomBuilderService {
  static getRoomOptions(cycleId) {
    return axios.all([
      http.get(`${Config.apiUrl}/capacitytypes?cycleId=${cycleId}`),
      http.get(`${Config.apiUrl}/seatings`),
      http.get(`${Config.apiUrl}/desktypes`),
      http.get(`${Config.apiUrl}/supervisordesk`),
      http.get(`${Config.apiUrl}/shapes`),
      http.get(`${Config.apiUrl}/facilities`),
    ]);
  }

  static getRooms() {
    return http.get(`${Config.apiUrl}/room`);
  }

  static getRoomAllInfo(roomId) {
    return http.get(`${Config.apiUrl}/roomAllInfo/${roomId}`);
  }

  static getRoom(roomId) {
    return http.get(`${Config.apiUrl}/room/${roomId}`);
  }

  static createRoom(room) {
    return http.post(`${Config.apiUrl}/room`, room);
  }

  static updateRoom(room) {
    return http.put(`${Config.apiUrl}/room`, room);
  }

  // STATEDIST-11174: autosave-safe variants of createRoom/updateRoom (STATEDIST-11173,
  // backend). `draft: true` tells the backend to skip its usual completeness validation
  // and merge the partial payload onto the existing room instead of blind-replacing it.
  // `autosaveToken` is a client-minted idempotency token resent unchanged on every
  // autosave call for a given draft, so a retried autosave can never create a duplicate
  // room. Neither field is sent by the plain createRoom/updateRoom above, so the final
  // Continue save (which uses those) is unaffected and remains a full, non-draft save.
  // STATEDIST-11175 follow-up: `draft: true` only relaxes the backend's completeness check
  // for an UPDATE merging onto an existing row — the very first create still 400s with
  // "Room structure fields seatingId or deskTypeId or shapeId or supervisorDeskId missing"
  // if none are present, since there's no existing row yet to merge onto. This call is only
  // ever reached for a brand-new room's very first autosave (from completing Room
  // Information, before Structure & Seating exists) — seating: {id: 1} is "Rows & Columns",
  // the same shape/default RoomStructure.jsx/SeatingDetails.jsx already preselect once the
  // coordinator reaches that step, so this doesn't fabricate a choice, it just sends the
  // pre-existing default a step earlier.
  static autosaveCreateRoom(room, autosaveToken) {
    return http.post(`${Config.apiUrl}/room`, { ...room, draft: true, autosaveToken, seating: { id: 1 } });
  }

  static autosaveUpdateRoom(room, autosaveToken) {
    return http.put(`${Config.apiUrl}/room`, { ...room, draft: true, autosaveToken });
  }

  static updateRoomTypeTiming(roomId, data) {
    return http.put(`${Config.apiUrl}/room/${roomId}/typetiming`, data);
  }

  static deleteRoom(roomId) {
    return http.delete(`${Config.apiUrl}/room/${roomId}`);
  }

  static getPastEvents(testCenterCycleId, eventId, params) {
    const queryString = params && isObject(params) ? `?${toQueryString(params)}` : '';
    return http.get(`${Config.apiUrl}/testcentercycle/${testCenterCycleId}/event/${eventId}/cycleevents${queryString}`);
  }

  static copyRooms(targetEventId, data) {
    return http.post(`${Config.apiUrl}/testcenterevent/${targetEventId}/copyroom`, data);
  }

  static updateRoomStatus(roomId, data) {
    return http.put(`${Config.apiUrl}/room/${roomId}/status`, data);
  }

  static submitRoomRanges(roomId) {
    return http.post(`${Config.apiUrl}/room/${roomId}/submitranges`);
  }

  static checkRoomMismatch(room) {
    return http.post(`${Config.apiUrl}/roommismatch`, room);
  }
  
  static getRoomDTO(roomId) {
    return http.get(`${Config.apiUrl}/room/${roomId}/minimaldto`)
  }

  static checkRoomNameExistence(locEvId, roomName) {
    return http.get(`${Config.apiUrl}/locationevent/${locEvId}/room-existence?name=${roomName}`)
  }
}

export default RoomBuilderService;
